import http from '@/utils/httpRequestHelper';

/**
 * Log in a user.
 * @param {Object} data - Login credentials.
 * @returns {Promise} - Promise resolving to the login response.
 */
export const login = (data) => http.postRequest('/login', data);

/**
 * Log out the current user.
 * @returns {Promise} - Promise resolving when the user is logged out.
 */
export const logout = () => http.postRequest('/logout');

/**
 * Register a new account.
 * @param {Object} data - Registration details.
 * @returns {Promise} - Promise resolving to the registration response.
 */
export const register = (data) => http.postRequest('/register', data);

/**
 * Get account information for the currently logged-in user.
 * @returns {Promise} - Promise resolving to the account information.
 */
export const getAccountInfo = () => http.getRequest('/ai/account/info');

/**
 * Get home information for the current user.
 * @returns {Promise} - Promise resolving to the home information.
 */
export const getHomeInfo = () => http.getRequest('/ai/home');


// Add these new API methods for account management
export const updateProfile = (profileData) => http.postRequest('/ai/account/update-profile', profileData);

export const changePassword = (passwordData) => http.postRequest('/ai/account/change-password', passwordData);

// OpenAPI Key Management
export const generateOpenApiKey = () => http.postRequest('/ai/account/openapi/generate');

export const getOpenApiKeyInfo = () => http.getRequest('/ai/account/openapi/info');

export const revokeOpenApiKey = () => http.postRequest('/ai/account/openapi/revoke');

// Token Balance APIs
export const getTokenBalance = () => {
    return http.getRequest('/ai/account/token-balance')
}

/**
 * Get paginated token history for the current logged-in account.
 * @param {object} params - { page, size }
 * @returns {Promise<PageResult<TokenHistory>>}
 */
export const getTokenHistory = (params = {}) => {
    return http.getRequest('/ai/account/token-history', params)
}

// Activation Code APIs
export const batchCreateActivationCodes = (data) => {
    const params = new URLSearchParams()
    params.append('codeCount', data.codeCount)
    params.append('tokenCount', data.tokenCount)
    params.append('expireTime', data.expireTime)

    return http.postRequest('/ai/account/activation-codes', params, {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        }
    })
}

// Batch create activation codes from CSV (one code per row, each with its own token count)
export const batchCreateProductActivationCodes = (codeList) => {
    return http.postRequest('/ai/account/activation-codes/product', codeList)
}

// usage: 0 = regular, 1 = product
export const listActivationCodes = (usage) => {
    return http.getRequest('/ai/account/activation-codes', { usage })
}

export const deleteActivationCode = (codeId) => {
    return http.deleteRequest(`/ai/account/activation-codes/${codeId}`)
}

export const generateInvitationCode = () => {
    return http.postRequest('/ai/account/invitation-code')
}

export const bindMasterAccount = (invitationCode) => {
    return http.postRequest(`/ai/account/bind-master/${invitationCode}`)
}

export const listSubAccounts = () => {
    return http.getRequest('/ai/account/sub-account')
}

// Additional APIs you might need to implement on backend
export const getMasterAccountInfo = (masterAccountId) => {
    return http.getRequest(`/ai/account/master/${masterAccountId}`)
}

export const adjustTokens = (param) => {
    return http.postRequest(`/ai/account/sub-account/token`, param)
}

/**
 * Redeem an activation code to top up token balance.
 * @param {string} activationCode - The activation code to redeem.
 * @returns {Promise}
 */
export const activateCode = (activationCode) => {
    return http.postRequest(`/ai/activation-code/activate/${activationCode}`)
}

// ─── Admin / Distributor APIs ──────────────────────────────────────────────

/**
 * Search an account by exact email match (admin only).
 * GET /api/v1/ai/account/search-by-email?email={email}
 * Returns { id, email, nickname, note } or 404 if not found.
 * @param {string} email
 * @returns {Promise<{ id, email, nickname, note }>}
 */
export const searchAccountByEmail = (email, phone, name) => {
    const params = {}
    if (email) params.email = email
    if (phone) params.phone = phone
    if (name) params.name = name   // exact nickname match
    return http.getRequest('/ai/account/search-by-email', params)
}

/**
 * Bind a target account as a distributor under the calling admin account.
 * POST /api/v1/ai/account/bind-distributor
 * masterId is derived server-side from the session — not sent in body.
 * @param {string} targetAccountId - ID of the account to bind.
 * @param {string} [note] - Optional note attached to the target account.
 * @returns {Promise}
 */
export const bindAccountAsDistributor = (targetAccountId, note) => {
    return http.postRequest('/ai/account/bind-distributor', {
        targetAccountId,
        note: note || ''
    })
}